import React, { useCallback, useEffect } from 'react';
import { CardViewContentBlockProps } from '../types';
import { Box, Typography } from '@mui/material';
import LibraryCategoryCard from '../../../common/library-category-card/LibraryCategoryCard';
import TerminationIcon from '../../../../assets/svg/termination.svg?react';
import PolicyIcon from '../../../../assets/svg/policies_and_procedures.svg?react';
import { useTranslation } from 'react-i18next';
import { TAGS_LABELS } from '../../../common/constants/tags-labels';
import DocumentListVault from '../../document-vault/components/document-list-vault/DocumentListVault';
import styles from '../EmployeeProfile.module.scss';
import RecruitmentIcon from '../../../../assets/svg/recruitment.svg?react';
import AgreementIcon from '../../../../assets/svg/policy.svg?react';
import NewEmployeeManualIcon from '../../../../assets/svg/new-employee-manual.svg?react';
import OnboardingIcon from '../../../../assets/svg/probation.svg?react';
import ProbationIcon from '../../../../assets/svg/probation.svg?react';
import EmployeeManagementIcon from '../../../../assets/svg/employee-management.svg?react';
import PerformanceManagementIcon from '../../../../assets/svg/performance.svg?react';
import ManagingUnderperformanceIcon from '../../../../assets/svg/growth.svg?react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ProgressOverlay } from '../../../common/progress-overlay/ProgressOverlay';
import { useAppSelector } from '../../../common/hooks/useAppSelector';
import { RootState } from '../../../store';
import { useDocumentsMetaData } from '../../document-vault/hooks/useDocumentsWorkflowsMetadata';
import { clearDocuments } from '../../../slices/documents/documentsSlice';
import { useAppDispatch } from '../../../common/hooks/useAppDispatch';
import OperationsIcon from '../../../../assets/svg/business-to-consumer-plan.svg?react';
import AccountingIcon from '../../../../assets/svg/view-list.svg?react';
import BadgeIcon from '../../../../assets/svg/badge.svg?react';

export const EmploymentCategoryContent = ({
  documents,
  workflow,
  setWorkflow,
  showWorkflowBreadcrumbs,
  setShowWorkflowBreadcrumbs,
}: CardViewContentBlockProps) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const dispatch = useAppDispatch();

  // Extract profileId from pathname
  const profileId = location.pathname.split('/employee-profiles/')[1]?.split('/')[0];

  const navigate = useNavigate();
  const workflowItem = searchParams.get('workflow');
  const documentUploadsLoading = useAppSelector((state: RootState) => state.documents.status);

  // Use metadata hook with profileId
  const { uploadedDocumentsMetaData } = useDocumentsMetaData(
    'All',
    'employment',
    Number(profileId)
  );

  const getDocsFilteredByEmptyWorkflowCount = () => {
    const metaData = uploadedDocumentsMetaData.find((el) => el.workflow === '');
    return metaData ? metaData.count : 0;
  };

  const getDocsCount = (workflow: string) => {
    const metaData = uploadedDocumentsMetaData.find((el) => el.workflow === workflow);
    return metaData
      ? metaData.count +
          (workflow === t('labels.generalAdministration')
            ? getDocsFilteredByEmptyWorkflowCount()
            : 0)
      : 0;
  };

  const hasNewDocuments = (workflow: string) => {
    const metaData = uploadedDocumentsMetaData.find((el) => el.workflow === workflow);
    return metaData ? metaData.isNew : false;
  };

  const getIcon = (workflow: string) => {
    switch (workflow) {
      case t('tagsLabels.documentWorkflow.employment.recruitment'):
        return <RecruitmentIcon />;
      case t('tagsLabels.documentWorkflow.employment.agreement'):
        return <AgreementIcon />;
      case t('tagsLabels.documentWorkflow.employment.newEmployeeManual'):
        return <NewEmployeeManualIcon />;
      case t('tagsLabels.documentWorkflow.employment.policiesAndProcedures'):
        return <PolicyIcon />;
      case t('tagsLabels.documentWorkflow.employment.induction'):
        return <OnboardingIcon />;
      case t('tagsLabels.documentWorkflow.employment.employeeManagement'):
        return <EmployeeManagementIcon />;
      case t('tagsLabels.documentWorkflow.employment.probation'):
        return <ProbationIcon />;
      case t('tagsLabels.documentWorkflow.employment.performanceManagement'):
        return <PerformanceManagementIcon />;
      case t('tagsLabels.documentWorkflow.employment.managingUnderperformance'):
        return <ManagingUnderperformanceIcon />;
      case t('tagsLabels.documentWorkflow.employment.accounting'):
        return <AccountingIcon />;
      case t('tagsLabels.documentWorkflow.employment.termination'):
        return <TerminationIcon />;
      case t('tagsLabels.documentWorkflow.employment.generalAdministration'):
        return <OperationsIcon />;
      case t('tagsLabels.documentWorkflow.employment.idCardsPassportsAndLicenses'):
        return <BadgeIcon />;
    }
  };

  const getWorkflowNumber = (workflow: string) => {
    switch (workflow) {
      case t('tagsLabels.documentWorkflow.employment.recruitment'):
        return '1. ';
      case t('tagsLabels.documentWorkflow.employment.agreement'):
        return '2. ';
      case t('tagsLabels.documentWorkflow.employment.newEmployeeManual'):
        return '3. ';
      case t('tagsLabels.documentWorkflow.employment.policiesAndProcedures'):
        return '4. ';
      case t('tagsLabels.documentWorkflow.employment.induction'):
        return '5. ';
      case t('tagsLabels.documentWorkflow.employment.employeeManagement'):
        return '6. ';
      case t('tagsLabels.documentWorkflow.employment.probation'):
        return '7. ';
      case t('tagsLabels.documentWorkflow.employment.performanceManagement'):
        return '8. ';
      case t('tagsLabels.documentWorkflow.employment.managingUnderperformance'):
        return '9. ';
      case t('tagsLabels.documentWorkflow.employment.termination'):
        return '10. ';
      case t('tagsLabels.documentWorkflow.employment.accounting'):
        return '11. ';
      case t('tagsLabels.documentWorkflow.employment.generalAdministration'):
        return '12. ';
      case t('tagsLabels.documentWorkflow.employment.idCardsPassportsAndLicenses'):
        return '13. ';
    }
  };

  const setWorkflowData = useCallback(() => {
    if (workflowItem) {
      setWorkflow(decodeURI(workflowItem));
      setShowWorkflowBreadcrumbs(true);
      navigate(location.pathname);
    }
  }, [workflowItem, navigate, location.pathname, setWorkflow, setShowWorkflowBreadcrumbs]);

  useEffect(() => {
    setWorkflowData();
  }, [setWorkflowData]);

  return (
    <>
      {!workflow && !showWorkflowBreadcrumbs && (
        <Typography
          variant='h3'
          component='h3'
          sx={{ marginBottom: '16px', fontSize: '15px', lineHeight: '24px' }}
        >
          {t('headers.workflows')}
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '24px',
          backgroundColor: 'transparent',
          flexWrap: 'wrap',
        }}
      >
        {workflow && showWorkflowBreadcrumbs ? (
          <>
            <Box className={styles.breadcrumbs}>
              <Typography
                variant='h3'
                component='h3'
                className={styles.documentViewText}
                onClick={() => {
                  setWorkflow(null);
                  setShowWorkflowBreadcrumbs(false);
                  dispatch(clearDocuments());
                }}
              >
                {t('headers.workflows')}
              </Typography>
              <Typography variant='body2' className={styles.separator}>
                ›
              </Typography>
              <Typography variant='body2' className={styles.documentViewText}>
                {workflow === t('headers.general') ? t('labels.generalUnassigned') : workflow}
              </Typography>
            </Box>
            <Box position='relative' width='100%'>
              <DocumentListVault
                documents={[...documents] /*getDocsFilteredByWorkflow(workflow)*/}
              />
              {documentUploadsLoading === 'loading' && (
                <ProgressOverlay className={'position-absolute'} />
              )}
            </Box>
          </>
        ) : (
          <>
            {TAGS_LABELS.documentWorkflow.employment.map((el) => (
              <LibraryCategoryCard
                title={getWorkflowNumber(t(el)) + t(el)}
                description={
                  getDocsCount(t(el)) +
                  ' document' +
                  (getDocsCount(t(el)) === 0 || getDocsCount(t(el)) > 1 ? 's' : '')
                }
                onClick={() => {
                  setWorkflow(t(el));
                  setShowWorkflowBreadcrumbs(true);
                }}
                icon={getIcon(t(el))}
                key={el}
                hasNewLabel={
                  el === 'tagsLabels.documentWorkflow.employment.generalAdministration'
                    ? hasNewDocuments(t(el)) || hasNewDocuments('')
                    : hasNewDocuments(t(el))
                }
              />
            ))}
          </>
        )}
      </Box>
    </>
  );
};
